import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Button from '../UI/Button';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { clientService } from '../../services/clientService';

interface Payment {
  id: string;
  clientId: string;
  clientName: string;
  amount: number;
  currency: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED';
  paymentMethod: string;
  transactionId?: string;
  description?: string;
  dueDate: string;
  paidDate?: string;
  createdAt: string;
  updatedAt: string;
}

interface PaymentFormData {
  clientId: string;
  amount: number;
  currency: string;
  status: Payment['status'];
  paymentMethod: string;
  transactionId?: string;
  description?: string;
  dueDate: string;
  paidDate?: string;
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  payment?: Payment | null;
  onSave: (payment: PaymentFormData) => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  payment,
  onSave,
}) => {
  const [loading, setLoading] = useState(false);
  const isEditing = !!payment;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<PaymentFormData>({
    defaultValues: {
      currency: 'USD',
      status: 'PENDING',
      paymentMethod: 'Credit Card',
    },
  });

  const watchedStatus = watch('status');

  useEffect(() => {
    if (payment) {
      reset({
        clientId: payment.clientId,
        amount: payment.amount,
        currency: payment.currency,
        status: payment.status,
        paymentMethod: payment.paymentMethod,
        transactionId: payment.transactionId || '',
        description: payment.description || '',
        dueDate: payment.dueDate ? payment.dueDate.split('T')[0] : new Date().toISOString().split('T')[0],
        paidDate: payment.paidDate ? payment.paidDate.split('T')[0] : '',
      });
    } else {
      reset({
        clientId: '',
        amount: 0,
        currency: 'USD',
        status: 'PENDING',
        paymentMethod: 'Credit Card',
        transactionId: '',
        description: '',
        dueDate: new Date().toISOString().split('T')[0],
        paidDate: '',
      });
    }
  }, [payment, reset]);

  const onSubmit = async (data: PaymentFormData) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      onSave(data);
    } catch (error) {
      console.error('Failed to save payment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      reset();
      onClose();
    }
  };

  // Load clients for selection
  const { data: clientsData } = useQuery({
    queryKey: ['clients', 'for-select'],
    queryFn: () => clientService.getClients({ page: 1, limit: 1000 }),
  });
  const clientOptions = (clientsData?.clients || []).map((c: any) => ({ id: c.id, name: `${c.firstName} ${c.lastName}` }));

  const paymentMethods = [
    'Credit Card',
    'Bank Transfer',
    'PayPal',
    'Cash',
    'Check',
    'Wire Transfer',
  ];

  const currencies = [
    'USD',
    'EUR',
    'GBP',
    'CAD',
    'AUD',
  ];

  const statusOptions = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'REFUNDED', label: 'Refunded' },
  ];

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    {isEditing ? 'Edit Payment' : 'Add New Payment'}
                  </Dialog.Title>
                  <button
                    onClick={handleClose}
                    disabled={loading}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Client Selection */}
                    <div>
                      <label className="form-label">
                        Client *
                      </label>
                      <select
                        {...register('clientId', { required: 'Client is required' })}
                        className="form-input"
                        disabled={loading}
                      >
                        <option value="">Select a client</option>
                        {clientOptions.map(client => (
                          <option key={client.id} value={client.id}>
                            {client.name}
                          </option>
                        ))}
                      </select>
                      {errors.clientId && (
                        <p className="form-error">{errors.clientId.message}</p>
                      )}
                    </div>

                    {/* Amount */}
                    <div>
                      <label className="form-label">
                        Amount *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        {...register('amount', { 
                          required: 'Amount is required',
                          min: { value: 0.01, message: 'Amount must be greater than 0' }
                        })}
                        className="form-input"
                        disabled={loading}
                      />
                      {errors.amount && (
                        <p className="form-error">{errors.amount.message}</p>
                      )}
                    </div>

                    {/* Currency */}
                    <div>
                      <label className="form-label">
                        Currency *
                      </label>
                      <select
                        {...register('currency', { required: 'Currency is required' })}
                        className="form-input"
                        disabled={loading}
                      >
                        {currencies.map(currency => (
                          <option key={currency} value={currency}>
                            {currency}
                          </option>
                        ))}
                      </select>
                      {errors.currency && (
                        <p className="form-error">{errors.currency.message}</p>
                      )}
                    </div>

                    {/* Payment Method */}
                    <div>
                      <label className="form-label">
                        Payment Method *
                      </label>
                      <select
                        {...register('paymentMethod', { required: 'Payment method is required' })}
                        className="form-input"
                        disabled={loading}
                      >
                        {paymentMethods.map(method => (
                          <option key={method} value={method}>
                            {method}
                          </option>
                        ))}
                      </select>
                      {errors.paymentMethod && (
                        <p className="form-error">{errors.paymentMethod.message}</p>
                      )}
                    </div>

                    {/* Status */}
                    <div>
                      <label className="form-label">
                        Status *
                      </label>
                      <select
                        {...register('status', { required: 'Status is required' })}
                        className="form-input"
                        disabled={loading}
                      >
                        {statusOptions.map(status => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                      {errors.status && (
                        <p className="form-error">{errors.status.message}</p>
                      )}
                    </div>

                    {/* Due Date */}
                    <div>
                      <label className="form-label">
                        Due Date *
                      </label>
                      <input
                        type="date"
                        {...register('dueDate', { required: 'Due date is required' })}
                        className="form-input"
                        disabled={loading}
                      />
                      {errors.dueDate && (
                        <p className="form-error">{errors.dueDate.message}</p>
                      )}
                    </div>

                    {/* Paid Date (only show if status is COMPLETED) */}
                    {watchedStatus === 'COMPLETED' && (
                      <div>
                        <label className="form-label">
                          Paid Date
                        </label>
                        <input
                          type="date"
                          {...register('paidDate')}
                          className="form-input"
                          disabled={loading}
                        />
                      </div>
                    )}

                    {/* Transaction ID */}
                    <div className={watchedStatus === 'COMPLETED' ? '' : 'md:col-span-2'}>
                      <label className="form-label">
                        Transaction ID
                      </label>
                      <input
                        type="text"
                        {...register('transactionId')}
                        className="form-input"
                        placeholder="Enter transaction ID"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="form-label">
                      Description
                    </label>
                    <textarea
                      {...register('description')}
                      rows={3}
                      className="form-input"
                      placeholder="Enter payment description"
                      disabled={loading}
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleClose}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      loading={loading}
                    >
                      {isEditing ? 'Update Payment' : 'Create Payment'}
                    </Button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PaymentModal;
